import React, { useEffect, useState } from "react";
import { WindowManagerProvider, useWM, WM_SESSION_KEY } from "./store";
import TopBar from "./TopBar";
import Dock from "./Dock";
import TabBar from "./TabBar";
import TabView from "./TabView";
import Spotlight from "./Spotlight";
import GestureLayer from "./GestureLayer";
import GestureHelp from "./GestureHelp";
import HeroLanding from "./HeroLanding";
import Taskbar from "./Taskbar";
import MobileNav from "./MobileNav";
import MeetingInvitationsWatcher from "./MeetingInvitationsWatcher";
import StickyNotes from "./StickyNotes";
import { useIsMobile } from "./useIsMobile";
import { APPS } from "./apps";
import { useAuth } from "./AuthContext";

function DesktopInner() {
  const { open, tileAll, cascadeAll, minimizeAll, windows, restoreSession, markSessionLoaded } = useWM();
  const { user, appAccess } = useAuth();
  const [spotOpen, setSpotOpen] = useState(false);
  const [stickyNotesVisible, setStickyNotesVisible] = useState(false);
  const isMobile = useIsMobile();

  // Open Chat app pre-filled when the user uses the hero "Ask anything…" input.
  const askHexa = (text) => {
    const chat = APPS.find((a) => a.id === "chat");
    if (chat) {
      open(chat);
      // Drop the prompt into a global slot the Chat app reads on mount.
      try {
        window.__HEXA_ASK_PRELOAD__ = text;
        window.dispatchEvent(new CustomEvent("hexa:ask", { detail: text }));
      } catch (e) { /* noop */ }
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSpotOpen(true);
      }
      // Cmd/Ctrl+Shift+T = tile, Cmd/Ctrl+Shift+C = cascade, Cmd/Ctrl+Shift+M = min all
      if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
        if (e.key.toLowerCase() === "t") { e.preventDefault(); tileAll(); }
        if (e.key.toLowerCase() === "d") { e.preventDefault(); cascadeAll(); }
        if (e.key.toLowerCase() === "m") { e.preventDefault(); minimizeAll(); }
      }
      if (e.key === "Escape") setSpotOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tileAll, cascadeAll, minimizeAll]);

  // Bring back the apps that were open before a page refresh.
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(WM_SESSION_KEY) || "[]");
      const items = (Array.isArray(saved) ? saved : [])
        .map((it) => {
          const found = APPS.find((a) => a.id === it.appId);
          return found?.redirect
            ? { ...it, app: found.redirect.app, props: { ...found.redirect.props, ...(it.props || {}) } }
            : { ...it, app: found };
        })
        .filter((it) => it.app
          && !(appAccess?.hidden || []).includes(it.app.id)
          && !(it.app.requiresSuperAdmin && !user?.is_super_admin));
      if (items.length) restoreSession(items);
    } catch (e) { /* corrupt or blocked storage — start clean */ }
    markSessionLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global "open a specific app" bus — used by MeetingInvitationsWatcher (Join Now),
  // HR quick-links, etc. Passes any `detail` props through as window props so
  // the receiving app can auto-navigate (e.g. auto-join a meeting room).
  useEffect(() => {
    const onOpen = (ev) => {
      const { id, ...props } = ev?.detail || {};
      if (!id) return;
      const app = APPS.find((a) => a.id === id);
      if (app) open(app, props);
    };
    window.addEventListener("hexa:open-app", onOpen);
    return () => window.removeEventListener("hexa:open-app", onOpen);
  }, [open]);

  // Show the hero "Start" overlay when:
  //   • no apps are open at all, OR
  //   • every open app is minimized (user clicked the HEXA Start button — they
  //     expect the launcher to appear so they can pick another app).
  const showLanding = windows.length === 0 || windows.every((w) => w.isMinimized);

  return (
    <div
      data-testid="desktop"
      className="relative h-screen w-screen overflow-hidden hex-grid-bg"
      style={{
        // Wallpaper restored and lightly brightened. Replacing it with a flat
        // colour removed it altogether, and lightening the whole shell was
        // reverted at the owner's request — dark chrome with the wallpaper
        // showing through is the intended look. The `hexa-light-shell` class is
        // deliberately not applied; its rules stay in index.css so the light
        // shell can be switched back on by re-adding the class here.
        backgroundImage:
          "linear-gradient(rgba(248,250,252,0.22), rgba(248,250,252,0.22)), url(" +
          (process.env.PUBLIC_URL || "") +
          "/desktop-wallpaper.jpg)",
      }}
    >
      <TopBar
        onOpenSpotlight={() => setSpotOpen(true)}
        hasOpenApps={windows.length > 0}
        stickyNotesVisible={stickyNotesVisible}
        onToggleStickyNotes={() => setStickyNotesVisible(v => !v)}
      />

      {stickyNotesVisible && !isMobile && (
        <StickyNotes
          visible={stickyNotesVisible}
          onToggle={() => setStickyNotesVisible(false)}
          currentUser={user}
        />
      )}

      {showLanding && <HeroLanding onAsk={askHexa} />}

      {/* Browser-style tabs: tab strip at the top, single visible tab pane below */}
      <TabBar />
      <TabView />

      {/* Desktop chrome — hidden on phone in favor of MobileNav.
          Taskbar is always mounted; its internal `collapsed` logic hides it
          off-screen when a sub-app window is open and reveals it on hover of
          the bottom edge. */}
      {!isMobile && <Taskbar />}
      {!isMobile && <Dock />}
      {isMobile && <MobileNav />}
      <GestureLayer />
      <GestureHelp />
      <MeetingInvitationsWatcher />
      <Spotlight open={spotOpen} onClose={() => setSpotOpen(false)} />
    </div>
  );
}

export default function Desktop() {
  return (
    <WindowManagerProvider>
      <DesktopInner />
    </WindowManagerProvider>
  );
}
